import { type AccountMeta, type Connection, PublicKey } from '@solana/web3.js';
import BN from 'bn.js';
import { estimateComputeUnits } from '../constants/cu-table.js';
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  CREATE_POOL_FEE_RECEIVER,
  MORFI_OPINION_PROGRAM_ID,
  SYSTEM_PROGRAM_ID,
  TOKEN_PROGRAM_ID,
} from '../constants/programs.js';
import { getProgram } from '../idl/loader.js';
import {
  deriveAmmAuthorityPda,
  deriveAmmLpMintPda,
  deriveAmmObservationPda,
  deriveAmmPoolStatePda,
  deriveAmmPoolVaultPda,
  sortMintPair,
} from '../pda/cp-swap.js';
import type { BuildParams, BuildResult } from '../types/build-result.js';
import { createBuildResult, createIdempotentAtaIx, invokeMethod } from './common.js';

const OUTCOME_SEED: Buffer = Buffer.from('outcome');
const BONDING_CURVE_SEED: Buffer = Buffer.from('bc');
const CLAIM_RIGHTS_MINT_SEED: Buffer = Buffer.from('claim_rights_mint');
const VAULT_SEED: Buffer = Buffer.from('vault');
const BC_QUOTE_VAULT_SEED: Buffer = Buffer.from('bc_quote');
const PRIZE_VAULT_SEED: Buffer = Buffer.from('prize');
const BACKER_SHARE_MINT_SEED: Buffer = Buffer.from('backer_share_mint');
const BACKER_FEE_VAULT_SEED: Buffer = Buffer.from('backer_fee');
const EVENT_AUTHORITY_SEED: Buffer = Buffer.from('__event_authority');

interface AnchorProgramAccounts {
  account: {
    market: { fetch(address: PublicKey): Promise<MarketAccountV2> };
    marketConfig: { fetch(address: PublicKey): Promise<MarketConfigAccountV2> };
  };
}

interface MarketAccountV2 {
  config: PublicKey;
  nOutcomes: number;
  outcomeMints: PublicKey[];
  winningOutcome: number | null;
}

interface MarketConfigAccountV2 {
  deployerAuthority: PublicKey;
  quoteMint: PublicKey;
  morfiAmmConfig: PublicKey;
  morfiAmmProgramId: PublicKey;
}

type GraduateSignerParams =
  | {
      cranker: PublicKey;
      admin?: never;
    }
  | {
      admin: PublicKey;
      cranker?: never;
    };

export type GraduateParams = BuildParams &
  GraduateSignerParams & {
    market: PublicKey;
    ammSeedTokensArg?: BN;
  };

export interface ClaimPrizeParams extends BuildParams {
  claimer: PublicKey;
  market: PublicKey;
  burnAmount: BN;
}

export interface ClaimBackerFeesParams extends BuildParams {
  claimer: PublicKey;
  market: PublicKey;
  burnAmount: BN;
}

function oneByteSeed(index: number): Buffer {
  if (!Number.isInteger(index) || index < 0 || index > 255) {
    throw new Error(`outcome index must fit in u8, got ${String(index)}`);
  }
  return Buffer.from([index]);
}

function derivePda(seeds: Buffer[], programId: PublicKey): PublicKey {
  return PublicKey.findProgramAddressSync(seeds, programId)[0];
}

function deriveAta(owner: PublicKey, mint: PublicKey, tokenProgram: PublicKey): PublicKey {
  return derivePda(
    [owner.toBuffer(), tokenProgram.toBuffer(), mint.toBuffer()],
    ASSOCIATED_TOKEN_PROGRAM_ID,
  );
}

function deriveOutcomeAccountPda(
  market: PublicKey,
  outcomeIndex: number,
  programId: PublicKey,
): PublicKey {
  return derivePda([OUTCOME_SEED, market.toBuffer(), oneByteSeed(outcomeIndex)], programId);
}

function deriveBondingCurvePda(
  market: PublicKey,
  outcomeIndex: number,
  programId: PublicKey,
): PublicKey {
  return derivePda([BONDING_CURVE_SEED, market.toBuffer(), oneByteSeed(outcomeIndex)], programId);
}

function deriveBcQuoteVaultPda(
  market: PublicKey,
  outcomeIndex: number,
  programId: PublicKey,
): PublicKey {
  return derivePda(
    [VAULT_SEED, BC_QUOTE_VAULT_SEED, market.toBuffer(), oneByteSeed(outcomeIndex)],
    programId,
  );
}

function derivePrizePoolVaultPda(market: PublicKey, programId: PublicKey): PublicKey {
  return derivePda([VAULT_SEED, PRIZE_VAULT_SEED, market.toBuffer()], programId);
}

function deriveBackerShareMintPda(market: PublicKey, programId: PublicKey): PublicKey {
  return derivePda([BACKER_SHARE_MINT_SEED, market.toBuffer()], programId);
}

function deriveBackerFeeVaultPda(market: PublicKey, programId: PublicKey): PublicKey {
  return derivePda([VAULT_SEED, BACKER_FEE_VAULT_SEED, market.toBuffer()], programId);
}

function deriveClaimRightsMintPda(
  market: PublicKey,
  outcomeIndex: number,
  programId: PublicKey,
): PublicKey {
  return derivePda(
    [CLAIM_RIGHTS_MINT_SEED, market.toBuffer(), oneByteSeed(outcomeIndex)],
    programId,
  );
}

function deriveEventAuthorityPda(programId: PublicKey): PublicKey {
  return derivePda([EVENT_AUTHORITY_SEED], programId);
}

async function fetchMarketAndConfig(
  connection: Connection,
  market: PublicKey,
  programId: PublicKey,
): Promise<{
  program: Awaited<ReturnType<typeof getProgram>>;
  marketAccount: MarketAccountV2;
  marketConfig: MarketConfigAccountV2;
}> {
  const program = await getProgram(connection, programId);
  // ALLOW-UNKNOWN: Anchor `Program<Idl>` and our typed `AnchorProgramAccounts` view of the same handle have structurally incompatible function signatures that TS cannot reconcile without `unknown`.
  const accounts = program as unknown as AnchorProgramAccounts;
  const marketAccount = await accounts.account.market.fetch(market);
  const marketConfig = await accounts.account.marketConfig.fetch(marketAccount.config);
  return { program, marketAccount, marketConfig };
}

function requireWinningOutcome(marketAccount: MarketAccountV2): number {
  const winner = marketAccount.winningOutcome;
  if (winner === null || winner === undefined) {
    throw new Error('market has no winning outcome');
  }
  if (!Number.isInteger(winner) || winner < 0 || winner >= marketAccount.nOutcomes) {
    throw new Error(
      `winning outcome ${String(winner)} outside market outcome count ${String(marketAccount.nOutcomes)}`,
    );
  }
  return winner;
}

function graduateCranker(params: GraduateParams): PublicKey {
  const cranker = 'cranker' in params ? params.cranker : params.admin;
  if (cranker === undefined) {
    throw new Error('buildGraduate requires a cranker signer');
  }
  return cranker;
}

function otherOutcomeBundles(
  market: PublicKey,
  nOutcomes: number,
  excludedOutcomeIndex: number,
  programId: PublicKey,
): AccountMeta[] {
  const remainingAccounts: AccountMeta[] = [];
  for (let i = 0; i < nOutcomes; i++) {
    if (i === excludedOutcomeIndex) {
      continue;
    }
    remainingAccounts.push({
      pubkey: deriveBcQuoteVaultPda(market, i, programId),
      isSigner: false,
      isWritable: true,
    });
  }
  return remainingAccounts;
}

export async function buildGraduate(
  connection: Connection,
  params: GraduateParams,
): Promise<BuildResult> {
  const programId = params.programId ?? MORFI_OPINION_PROGRAM_ID;
  const cranker = graduateCranker(params);
  const { program, marketAccount, marketConfig } = await fetchMarketAndConfig(
    connection,
    params.market,
    programId,
  );
  const winner = requireWinningOutcome(marketAccount);
  const winningMint = marketAccount.outcomeMints[winner];
  if (winningMint === undefined) {
    throw new Error(`winning outcome ${String(winner)} does not exist`);
  }

  const ammProgram = marketConfig.morfiAmmProgramId;
  const [mint0, mint1] = sortMintPair(marketConfig.quoteMint, winningMint);
  const poolState = deriveAmmPoolStatePda(marketConfig.morfiAmmConfig, mint0, mint1, ammProgram);
  const lpMint = deriveAmmLpMintPda(poolState, ammProgram);
  const crankerToken0 = deriveAta(cranker, mint0, TOKEN_PROGRAM_ID);
  const crankerToken1 = deriveAta(cranker, mint1, TOKEN_PROGRAM_ID);

  const instruction = await invokeMethod(
    program,
    'resolveDrainAndMigrate',
    params.ammSeedTokensArg ?? new BN(0),
  )
    .accounts({
      cranker,
      market: params.market,
      marketConfig: marketAccount.config,
      quoteMint: marketConfig.quoteMint,
      winningBc: deriveBondingCurvePda(params.market, winner, programId),
      winningOutcome: deriveOutcomeAccountPda(params.market, winner, programId),
      winningBcQuoteVault: deriveBcQuoteVaultPda(params.market, winner, programId),
      winningBcTokenVault: deriveAta(params.market, winningMint, TOKEN_PROGRAM_ID),
      winningOutcomeMint: winningMint,
      winningClaimRightsMint: deriveClaimRightsMintPda(params.market, winner, programId),
      prizePoolVault: derivePrizePoolVaultPda(params.market, programId),
      ammProgram,
      ammConfig: marketConfig.morfiAmmConfig,
      ammAuthority: deriveAmmAuthorityPda(ammProgram),
      ammPoolState: poolState,
      ammLpMint: lpMint,
      crankerToken0,
      crankerToken1,
      crankerLpToken: deriveAta(cranker, lpMint, TOKEN_PROGRAM_ID),
      marketLpLockToken: deriveAta(params.market, lpMint, TOKEN_PROGRAM_ID),
      ammToken0Vault: deriveAmmPoolVaultPda(poolState, mint0, ammProgram),
      ammToken1Vault: deriveAmmPoolVaultPda(poolState, mint1, ammProgram),
      ammCreatePoolFee: CREATE_POOL_FEE_RECEIVER,
      ammObservationState: deriveAmmObservationPda(poolState, ammProgram),
      systemProgram: SYSTEM_PROGRAM_ID,
      tokenProgram: TOKEN_PROGRAM_ID,
      eventAuthority: deriveEventAuthorityPda(programId),
      program: programId,
    })
    .remainingAccounts(
      otherOutcomeBundles(params.market, marketAccount.nOutcomes, winner, programId),
    )
    .instruction();

  const sourceAtaInstructions = [
    createIdempotentAtaIx(cranker, cranker, mint0, TOKEN_PROGRAM_ID),
    createIdempotentAtaIx(cranker, cranker, mint1, TOKEN_PROGRAM_ID),
  ];

  return createBuildResult(
    [...sourceAtaInstructions, instruction],
    [cranker],
    params.computeUnits ?? estimateComputeUnits('resolve_drain_and_migrate'),
  );
}

export async function buildClaimPrize(
  connection: Connection,
  params: ClaimPrizeParams,
): Promise<BuildResult> {
  const programId = params.programId ?? MORFI_OPINION_PROGRAM_ID;
  const { program, marketAccount, marketConfig } = await fetchMarketAndConfig(
    connection,
    params.market,
    programId,
  );
  const winner = requireWinningOutcome(marketAccount);
  const claimRightsMint = deriveClaimRightsMintPda(params.market, winner, programId);
  const claimerQuoteAta = deriveAta(params.claimer, marketConfig.quoteMint, TOKEN_PROGRAM_ID);

  const instruction = await invokeMethod(program, 'claimPrize', params.burnAmount)
    .accounts({
      claimer: params.claimer,
      market: params.market,
      marketConfig: marketAccount.config,
      quoteMint: marketConfig.quoteMint,
      claimRightsMint,
      claimerClaimRightsAta: deriveAta(params.claimer, claimRightsMint, TOKEN_PROGRAM_ID),
      claimerQuoteAta,
      prizePoolVault: derivePrizePoolVaultPda(params.market, programId),
      tokenProgram: TOKEN_PROGRAM_ID,
      systemProgram: SYSTEM_PROGRAM_ID,
      associatedTokenProgram: ASSOCIATED_TOKEN_PROGRAM_ID,
      eventAuthority: deriveEventAuthorityPda(programId),
      program: programId,
    })
    .instruction();

  return createBuildResult(
    [
      createIdempotentAtaIx(
        params.claimer,
        params.claimer,
        marketConfig.quoteMint,
        TOKEN_PROGRAM_ID,
      ),
      instruction,
    ],
    [params.claimer],
    params.computeUnits ?? estimateComputeUnits('claim_prize'),
  );
}

export async function buildClaimBackerFees(
  connection: Connection,
  params: ClaimBackerFeesParams,
): Promise<BuildResult> {
  const programId = params.programId ?? MORFI_OPINION_PROGRAM_ID;
  const { program, marketAccount, marketConfig } = await fetchMarketAndConfig(
    connection,
    params.market,
    programId,
  );
  const backerShareMint = deriveBackerShareMintPda(params.market, programId);
  const claimerQuoteAta = deriveAta(params.claimer, marketConfig.quoteMint, TOKEN_PROGRAM_ID);

  const instruction = await invokeMethod(program, 'claimBackerFees', params.burnAmount)
    .accounts({
      claimer: params.claimer,
      market: params.market,
      marketConfig: marketAccount.config,
      quoteMint: marketConfig.quoteMint,
      backerShareMint,
      claimerShareAta: deriveAta(params.claimer, backerShareMint, TOKEN_PROGRAM_ID),
      claimerQuoteAta,
      backerFeeVault: deriveBackerFeeVaultPda(params.market, programId),
      tokenProgram: TOKEN_PROGRAM_ID,
      systemProgram: SYSTEM_PROGRAM_ID,
      associatedTokenProgram: ASSOCIATED_TOKEN_PROGRAM_ID,
      eventAuthority: deriveEventAuthorityPda(programId),
      program: programId,
    })
    .instruction();

  return createBuildResult(
    [
      createIdempotentAtaIx(
        params.claimer,
        params.claimer,
        marketConfig.quoteMint,
        TOKEN_PROGRAM_ID,
      ),
      instruction,
    ],
    [params.claimer],
    params.computeUnits ?? estimateComputeUnits('claim_backer_fees'),
  );
}
